import colors from "common/constants/colorsOld";
import stringApplySearch from "common/utils/stringApplySearch";
import type { FunctionComponent } from "react";
import { useState, useDeferredValue, useEffect } from "react";
import Input from "ui/forms/Input";
import Select from "ui/forms/Select";
import type { SvgIconProps } from "ui/icons/SVGIcon";
import ComponentSection from "ui/library/ComponentSection";
import styles from "./icons.module.scss";

type IconModule = { default: FunctionComponent<SvgIconProps> };
type IconMap = [string, FunctionComponent<SvgIconProps>][];

const iconModules = import.meta.glob<IconModule>("../icons/Icon*.tsx");

export default function IconsComponent() {
  const [iconMap, setIconMap] = useState<IconMap>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [fillColor, setFillColor] = useState<string>("");
  const [strokeColor, setStrokeColor] = useState<string>("");
  const deferredSearchTerm = useDeferredValue(searchTerm);

  useEffect(() => {
    const entries = Object.entries(iconModules);
    void Promise.all(
      entries.map(async ([path, loader]) => {
        const iconName = path.replace(/^.*\/(Icon.*?)\.tsx$/, "$1");
        const mod = await loader();
        return [iconName, mod.default] as [string, FunctionComponent<SvgIconProps>];
      }),
    )
      .then(setIconMap)
      .catch((err: unknown) => console.error("Failed to load icons", err));
  }, []);

  const filteredIcons = iconMap.filter(([iconName]) =>
    stringApplySearch(iconName.toLowerCase(), deferredSearchTerm.toLowerCase()),
  );

  const colorOptions = [
    { value: "", label: "None" },
    { value: colors.stoneDarkX1, label: "Foreground" },
    { value: colors.stone, label: "Subdued" },
    { value: colors.stoneLight, label: "Nonessential" },
    { value: colors.emerald, label: "Brand Green" },
    { value: colors.ruby, label: "Danger" },
    { value: colors.amber, label: "Warning" },
    { value: colors.emerald, label: "Success" },
  ];

  return (
    <ComponentSection>
      <header>
        <h5>Search and Filter</h5>
        <label htmlFor="">
          Search Icons
          <Input
            type="text"
            placeholder="Search for icons..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </label>
        <label>
          Fill Color
          <Select options={colorOptions} value={fillColor} onChange={(e) => setFillColor(String(e))} />
        </label>
        <label htmlFor="">
          Stroke Color
          <Select options={colorOptions} value={strokeColor} onChange={(e) => setStrokeColor(String(e))} />
        </label>
      </header>
      <div className={styles.iconGrid}>
        {filteredIcons.map(([iconName, IconComponent]) => (
          <div key={iconName} className={styles.iconCard}>
            <div className={styles.iconWrapper}>
              <IconComponent fill={fillColor ?? undefined} stroke={strokeColor ?? undefined} width={24} height={24} />
            </div>
            <span className={styles.iconName}>{iconName}</span>
          </div>
        ))}
      </div>
      {filteredIcons.length === 0 && (
        <div className={styles.noResults}>
          <p>No icons found matching &ldquo;{deferredSearchTerm}&rdquo;</p>
        </div>
      )}
    </ComponentSection>
  );
}
